import { Form } from 'react-bootstrap';
import { type DpQuantity } from '@/infrastructure/CompetitorV2Client';

type Dimension = 'mass' | 'volume' | 'count';
type Unit = 'g' | 'kg' | 'ml' | 'l' | 'pad' | 'sheet' | 'capsule' | 'piece';
export type QuantityDraft = {
  dimension: Dimension | '';
  unit: Unit | '';
  unitQuantity: string;
  packCount: string;
};

const UNITS = { mass: ['g', 'kg'], volume: ['ml', 'l'], count: ['pad', 'sheet', 'capsule', 'piece'] } as const;
const empty = (): QuantityDraft => ({
  dimension: '',
  unit: '',
  unitQuantity: '',
  packCount: '',
});
export const dpQuantityDraft = (input?: DpQuantity | null): QuantityDraft =>
  input
    ? {
        ...empty(),
        dimension: ['g', 'kg'].includes(input.unit) ? 'mass' : ['ml', 'L'].includes(input.unit) ? 'volume' : 'count',
        unit: (input.unit === 'L' ? 'l' : input.unit) as QuantityDraft['unit'],
        unitQuantity: String(input.unitCount),
        packCount: String(input.packCount),
      }
    : empty();
export const dpQuantityInput = (draft: QuantityDraft): DpQuantity | null => {
  const unitCount = Number(draft.unitQuantity);
  const packCount = Number(draft.packCount);
  if (
    !draft.dimension ||
    !UNITS[draft.dimension].includes(draft.unit as never) ||
    !Number.isFinite(unitCount) ||
    unitCount <= 0 ||
    !Number.isInteger(packCount) ||
    packCount <= 0 ||
    (draft.dimension === 'count' && !Number.isInteger(unitCount))
  ) {
    return null;
  }
  return { unitCount, unit: draft.unit === 'l' ? 'L' : draft.unit, packCount };
};

export function QuantityFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: QuantityDraft;
  onChange: (next: QuantityDraft) => void;
}) {
  const units = value.dimension ? UNITS[value.dimension] : [];
  return (
    <fieldset className="mb-2">
      <legend className="fs-6">{label} Quantity</legend>
      <div className="d-flex flex-wrap gap-2">
        <Form.Select
          aria-label={`${label} Dimension`}
          className="w-auto"
          required
          value={value.dimension}
          onChange={(event) => onChange({ ...value, dimension: event.target.value as Dimension | '', unit: '' })}
        >
          <option value="">Select dimension</option>
          <option value="mass">Mass</option>
          <option value="volume">Volume</option>
          <option value="count">Count</option>
        </Form.Select>
        <Form.Select
          aria-label={`${label} Unit`}
          className="w-auto"
          required
          value={value.unit}
          onChange={(event) => onChange({ ...value, unit: event.target.value as Unit | '' })}
        >
          <option value="">Select unit</option>
          {units.map((unit) => (
            <option key={unit} value={unit}>
              {unit === 'l' ? 'L' : unit}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          aria-label={`${label} Quantity per unit`}
          className="w-auto"
          type="number"
          min="0.000001"
          step={value.dimension === 'count' ? '1' : '0.000001'}
          required
          value={value.unitQuantity}
          onChange={(event) => onChange({ ...value, unitQuantity: event.target.value })}
        />
        <Form.Control
          aria-label={`${label} Pack count`}
          className="w-auto"
          type="number"
          min="1"
          step="1"
          required
          value={value.packCount}
          onChange={(event) => onChange({ ...value, packCount: event.target.value })}
        />
      </div>
    </fieldset>
  );
}
